<template>
  <div class="flex flex-col items-stretch gap-16">
    <InputAddressNationality
      v-if="withNationality"
      data-test="nationality"
      :disabled="disabled"
      :error="errors.nationality"
      :value="nationality"
      @change="handleNationalityInput"
    />

    <InputAddressBirthdate
      v-if="withBirthdate"
      data-test="birthdate"
      :disabled="disabled"
      :error="errors.birthdate"
      :value="formattedBirthdate"
      @input="handleBirthdateInput"
    />

    <InputAddressNationalId
      v-if="withNationalId && country"
      :country="country"
      data-test="nationalId"
      :error="errors.nationalId"
      :value="nationalId"
      @input="handleNationalIdInput"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'

import { formatDateToDDMMYYYY } from '@backmarket/utils/date/formatDateToDDMMYYYY'
import { formatDateToYYYYMMDD } from '@backmarket/utils/date/formatDateToYYYYMMDD'

import { NationalIdCountries } from '../../types'

import { InputAddressBirthdate } from './InputAddressBirthdate'
import { InputAddressNationalId } from './InputAddressNationalId'
import { InputAddressNationality } from './InputAddressNationality'

const props = withDefaults(
  defineProps<{
    birthdate?: string
    nationality?: string
    nationalId?: string
    disabled?: boolean
    country?: NationalIdCountries
    errors: { nationality?: string; nationalId?: string; birthdate?: string }
    withNationality?: boolean
    withNationalId?: boolean
    withBirthdate?: boolean
  }>(),
  {
    birthdate: '',
    nationality: '',
    nationalId: '',
    country: undefined,
    disabled: false,
    withBirthdate: true,
    withNationalId: false,
    withNationality: true,
  },
)

const emit = defineEmits([
  'update:birthdate',
  'update:nationality',
  'update:nationalId',
])

const isDateSupported = ref(true)

onMounted(() => {
  // we need to check that type="date" is supported by the browser else
  // we will format manually the date to display
  const input = document.createElement('input')
  const value = 'a'
  input.setAttribute('type', 'date')
  input.setAttribute('value', value)
  isDateSupported.value = input.value !== value
})

const formattedBirthdate = computed(() => {
  try {
    return isDateSupported.value
      ? props.birthdate
      : formatDateToDDMMYYYY(props.birthdate)
  } catch {
    return props.birthdate
  }
})

const handleBirthdateInput = (event: Event) => {
  const target = event.target as HTMLInputElement

  try {
    emit(
      'update:birthdate',
      target.value.includes('-')
        ? target.value
        : formatDateToYYYYMMDD(target.value),
    )
  } catch {
    emit('update:birthdate', target.value)
  }
}

const handleNationalityInput = (event: Event) =>
  emit('update:nationality', (event.target as HTMLInputElement).value)

const handleNationalIdInput = (event: Event) =>
  emit('update:nationalId', (event.target as HTMLInputElement).value)
</script>
